import type { CheckoutBridgeService } from '@/scopes/checkout/domain/types/services/bridge'
import type { BridgeOptionViewModel, BridgeParticipantViewModel } from './types'

/**
 * Transforms a `CheckoutBridgeService` into an option-centric list of
 * `BridgeOptionViewModel` rows — one row per Bridge option/activity.
 *
 * The legacy `booking-filterable-services` widget with `ParticipantsAsOptions`
 * componentType creates one collapsible row per option. This selector replicates
 * that mapping from the per-participant data shape.
 */
export function bridgeOptionsSelector(service: CheckoutBridgeService): BridgeOptionViewModel[] {
  const { options } = service

  return Object.values(options).map((option) => {
    const availableParticipants = getAvailableParticipantsForOption(service, option.id)
    const assignedParticipantIds = getAssignedParticipantIdsForOption(service, option.id)

    return {
      id: option.id,
      code: option.code,
      label: option.label,
      price: option.price,
      priceAssignments: option.priceAssignments ?? [],
      isActive: assignedParticipantIds.length > 0,
      availableParticipants,
      assignedParticipantIds,
    }
  })
}

/**
 * Returns the list of participants that can select the given option,
 * annotated with whether they are currently assigned to it.
 */
export function getAvailableParticipantsForOption(
  service: CheckoutBridgeService,
  optionId: string,
): BridgeParticipantViewModel[] {
  return Object.values(service.participants)
    .filter(participant => participant.availableOptions.includes(optionId))
    .map(participant => ({
      id: participant.id,
      name: participant.name,
      isChecked: participant.selectedValues.includes(optionId),
    }))
}

/**
 * Returns the IDs of participants currently assigned to the given option.
 */
export function getAssignedParticipantIdsForOption(
  service: CheckoutBridgeService,
  optionId: string,
): string[] {
  return Object.values(service.participants)
    .filter(participant => participant.selectedValues.includes(optionId))
    .map(participant => participant.id)
}

/**
 * Returns `true` if there are multiple participants in the service,
 * used to decide whether to show individual participant names.
 */
export function shouldShowParticipantNames(service: CheckoutBridgeService): boolean {
  return Object.keys(service.participants).length > 1
}
